import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import Address from "@mapmycustomers/shared/types/Address";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import CustomFieldValue from "@mapmycustomers/shared/types/customField/CustomFieldValue";
import {
  EntitiesSupportingLeadGen,
  EntitiesSupportingRoutes,
  EntityTypesSupportingRoutes,
  EntityTypeSupportingLeadGen,
  Group,
  Lead,
  Person,
} from "@mapmycustomers/shared/types/entity";
import { DuplicateEntity } from "@mapmycustomers/shared/types/entity/Duplicate";
import Route from "@mapmycustomers/shared/types/entity/Route";
import UpdateCompanyPayload from "@mapmycustomers/shared/types/entity/UpdateCompanyPayload";
import UpdatePersonPayload from "@mapmycustomers/shared/types/entity/UpdatePersonPayload";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import PersistentLeadFinderSettings from "@mapmycustomers/shared/types/persistent/PersistentLeadFinderSettings";

import LeadFinderLimit from "@app/types/map/LeadFinderLimit";
import MapViewportState from "@app/types/map/MapViewportState";

import RecordRouteModel from "../types/RecordRouteModel";

import { LeadFinderProgressState, LeadFinderQueryState } from "./LeadFinderState";

export const startLeadFinderSearch = createAsyncAction(
  "leadFinder/startSearch/request",
  "leadFinder/startSearch/success",
  "leadFinder/startSearch/failure"
)<
  {
    callback?: (bounds?: google.maps.LatLngBounds) => void;
    onBeforeSearch?: () => void;
    onLimitThresholdReached?: (limit: LeadFinderLimit) => void;
    onSearchSuccess?: (bounds?: google.maps.LatLngBounds) => void;
    refresh?: boolean;
    search: string;
    viewport?: MapViewportState;
  },
  {
    records: Array<Lead>;
    totalFilteredRecords: number;
    totalRecords: number;
  },
  void
>();
export const cancelLeadFinderSearch = createAction("leadFinder/cancelSearch")<void>();

export const setAddRecordsVisibility = createAction("leadFinder/setAddRecordsVisibility")<{
  visible: boolean;
}>();
export const initializeAddRecords = createAsyncAction(
  "leadFinder/initializeAddRecords/request",
  "leadFinder/initializeAddRecords/success",
  "leadFinder/initializeAddRecords/failure"
)<
  {
    entityType: EntityTypeSupportingLeadGen;
  },
  {
    routes: Route[];
  },
  void
>();

export const filterRoutes = createAsyncAction(
  "leadFinder/filterRoutes/request",
  "leadFinder/filterRoutes/success",
  "leadFinder/filterRoutes/failure"
)<
  {
    entityType: EntityTypesSupportingRoutes;
    query?: string;
  },
  Route[],
  void
>();

export const submitAddRecords = createAsyncAction(
  "leadFinder/submitAddRecords/request",
  "leadFinder/submitAddRecords/success",
  "leadFinder/submitAddRecords/failure"
)<
  {
    customFields?: CustomFieldValue[];
    entity: UpdateCompanyPayload | UpdatePersonPayload;
    entityType: EntityTypeSupportingLeadGen;
    groupIds?: Group["id"][];
    layoutId: FormLayout["id"];
    onClose?: () => void;
    onComplete?: () => void;
    onRecordsCreated?: (
      entityIds: Array<EntitiesSupportingLeadGen["id"]>,
      entityType: EntityTypeSupportingLeadGen
    ) => void;
    personId?: Person["id"];
    routeIdsOrRoutePayloads?: RecordRouteModel[];
    selectedLeads: Lead[];
  },
  void,
  void
>();
export const addRecordsToRoute = createAsyncAction(
  "leadFinder/addRecordsToRoute/request",
  "leadFinder/addRecordsToRoute/success",
  "leadFinder/addRecordsToRoute/failure"
)<
  {
    entityIds: EntitiesSupportingRoutes["id"][];
    entityType: EntityTypeSupportingLeadGen;
    routeId: Route["id"];
  },
  {
    isNewRoute?: boolean;
    routeId: Route["id"];
  },
  {
    errors: Array<string>;
    routeId: Route["id"];
  }
>();

export const hideLeads = createAsyncAction(
  "leadFinder/blacklist/request",
  "leadFinder/blacklist/success",
  "leadFinder/blacklist/failure"
)<
  {
    placeIds: Lead["place_id"][];
  },
  {
    placeIds: Lead["place_id"][];
  },
  void
>();

export const showHiddenLeads = createAsyncAction(
  "leadFinder/showHiddenLeads/request",
  "leadFinder/showHiddenLeads/success",
  "leadFinder/showHiddenLeads/failure"
)<
  {
    placeIds: Lead["place_id"][];
  },
  {
    placeIds: Lead["place_id"][];
  },
  void
>();

export const finishAddRecords = createAction("leadFinder/finishAddRecords")<void>();
export const updateAddRecordsProgress = createAction(
  "leadFinder/updateAddRecordsProgress"
)<LeadFinderProgressState>();

export const showLeadFinder = createAction("leadFinder/show")<void>();
export const hideLeadFinder = createAction("leadFinder/hide")<void>();
export const toggleLeadFinder = createAction("leadFinder/toggle")<void>();
export const setConfigurationVisibility = createAction("leadFinder/setConfigurationVisibility")<{
  visible: boolean;
}>();

export const setOtherPins = createAction("leadFinder/setOtherPins")<{
  otherPins: boolean;
}>();

export const showDuplicates = createAction("leadFinder/showDuplicates")<{
  primaryRecord?: Lead;
  records?: DuplicateEntity[];
}>();
export const hideDuplicates = createAction("leadFinder/hideDuplicates")<void>();
export const setPrimaryRecord = createAction("leadFinder/setPrimaryRecord")<{
  primaryRecord: Lead;
}>();
export const setLeadFinderQuery = createAction("leadFinder/setLeadFinderQuery")<
  Partial<LeadFinderQueryState>
>();

export const geocodeLocation = createAsyncAction(
  "leadFinder/geocodeLocation/request",
  "leadFinder/geocodeLocation/success",
  "leadFinder/geocodeLocation/failure"
)<
  {
    point: LongLat;
  },
  {
    address?: Address;
    location?: string;
  },
  void
>();
export const clearLeadFinderLocation = createAction("leadFinder/clearLeadFinderLocation")<void>();
export const clearLeadFinderRecords = createAction("leadFinder/clearLeadFinderRecords")<void>();
export const removeLeadFinderRecords = createAction("leadFinder/removeLeadFinderRecords")<{
  placeIds: Lead["place_id"][];
}>();

export const initializeLeadFinderMode = createAsyncAction(
  "leadFinder/initialize/request",
  "leadFinder/initialize/success",
  "leadFinder/initialize/failure"
)<
  {
    callback?: (bounds?: google.maps.LatLngBounds) => void;
  },
  void,
  void
>();
export const enterLeadFinderMode = createAction("leadFinder/enter")<void>();
export const exitLeadFinderMode = createAction("leadFinder/exit")<void>();

export const saveSearch = createAction("leadFinder/saveSearch")<{
  search: string;
}>();
export const removeSearchHistoryItem = createAction("leadFinder/removeSearchHistoryItem")<{
  search: string;
}>();
export const setSearchHistory = createAction("leadFinder/setSearchHistory")<
  Partial<PersistentLeadFinderSettings>
>();
export const showSavedSearchesModal = createAction("leadFinder/showSavedSearchesModal")<void>();
export const hideSavedSearchesModal = createAction("leadFinder/hideSavedSearchesModal")<void>();

export const toggleHiddenLeadsVisibility = createAction(
  "leadFinder/toggleHiddenLeadsVisibility"
)<void>();
export const toggleDuplicateLeadsVisibility = createAction(
  "leadFinder/toggleDuplicateLeadsVisibility"
)<void>();

export const setCenterPoint = createAction("leadFinder/setCenterPoint")<LongLat | undefined>();
export const setCurrentBounds = createAction("leadFinder/setCurrentBounds")<{
  bounds?: google.maps.LatLngBounds;
}>();

export const setCustomSearchDistances = createAction("leadFinder/setCustomSearchDistances")<{
  customDistances: Array<number>;
}>();
export const setSearchDistance = createAction("leadFinder/setSearchDistance")<{
  distance: number;
}>();
export const setDistanceSettings = createAction("leadFinder/setDistanceSettings")<
  Partial<Pick<PersistentLeadFinderSettings, "customDistances" | "searchDistance">>
>();

export const setLeadAnnotation = createAction("leadFinder/setLeadAnnotation")<
  Lead["place_id"] | undefined
>();

export type LeadFinderActions = ActionType<
  | typeof addRecordsToRoute
  | typeof cancelLeadFinderSearch
  | typeof clearLeadFinderLocation
  | typeof clearLeadFinderRecords
  | typeof enterLeadFinderMode
  | typeof exitLeadFinderMode
  | typeof filterRoutes
  | typeof finishAddRecords
  | typeof geocodeLocation
  | typeof hideDuplicates
  | typeof hideLeadFinder
  | typeof hideLeads
  | typeof hideSavedSearchesModal
  | typeof initializeAddRecords
  | typeof initializeLeadFinderMode
  | typeof removeLeadFinderRecords
  | typeof removeSearchHistoryItem
  | typeof saveSearch
  | typeof setAddRecordsVisibility
  | typeof setCenterPoint
  | typeof setConfigurationVisibility
  | typeof setCurrentBounds
  | typeof setCustomSearchDistances
  | typeof setDistanceSettings
  | typeof setLeadAnnotation
  | typeof setLeadFinderQuery
  | typeof setOtherPins
  | typeof setPrimaryRecord
  | typeof setSearchDistance
  | typeof setSearchHistory
  | typeof showDuplicates
  | typeof showHiddenLeads
  | typeof showLeadFinder
  | typeof showSavedSearchesModal
  | typeof startLeadFinderSearch
  | typeof submitAddRecords
  | typeof toggleDuplicateLeadsVisibility
  | typeof toggleHiddenLeadsVisibility
  | typeof toggleLeadFinder
  | typeof updateAddRecordsProgress
>;
